import React, { useState, useMemo, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import AnnouncementTypeTable from './components/AnnouncementTypeTable';
import AnnouncementTypeForm from './components/AnnouncementTypeForm';
import useAnnouncementTypeData from './hooks/useAnnouncementTypeData';
import GenericTableFilter from '../../../components/common/GenericTableFilter';
import { useLanguage } from '../../../contexts/LanguageContext';

const AnnouncementTypePage = () => {
  const { th } = useLanguage();
  const [selectedItem, setSelectedItem] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const toast = useRef(null);
  const [isEdit, setIsEdit] = useState(false);

  const {
    announcementTypeApi,
    isLoading,
    lazyParams,
    onPage,
    saveAnnouncementType,
    activeFilters,
    handleFilterChange,  
    initFilters,
    deleteAnnouncementType,
    refetch,
  } = useAnnouncementTypeData();

  const handleEdit = (rowData) => {
    setIsEdit(true);
    setSelectedItem({
      ...rowData,
      type: rowData?.type != null ? Number(rowData.type) : null,
    });
    setDialogVisible(true);
  };

  
  const TYPE_OPTIONS = useMemo(
    () => [
      { value: 132, label: th('announcements.typeLabels.spareParts') },
      { value: 131, label: th('announcements.typeLabels.warranty') },
      { value: 130, label: th('announcements.typeLabels.afterSales') },
      { value: 129, label: th('announcements.typeLabels.marketing') },
      { value: 128, label: th('announcements.typeLabels.sales') },
    ],
    [th]
  );

 
  const toNumberType = (raw) => {
    if (raw == null || raw === '') return '';
    if (Array.isArray(raw)) return toNumberType(raw[0]);

    if (typeof raw === 'object') {
      if ('value' in raw) return Number(raw.value);
      if ('id' in raw) return Number(raw.id);
      if ('label' in raw) {
        const f = TYPE_OPTIONS.find(o => o.label === raw.label);
        return f ? Number(f.value) : '';
      }
      return '';
    }

    if (typeof raw === 'string') {
      const n = Number(raw);
      if (!Number.isNaN(n)) return n;
      const f = TYPE_OPTIONS.find(o => o.label === raw);
      return f ? Number(f.value) : '';
    }

    if (typeof raw === 'number') return raw;
    return '';
  };

  const handleHide = () => {
    setDialogVisible(false);
    setSelectedItem(null);
    setIsEdit(false);
  };

  const handleSubmit = async (formData) => {
    const success = await saveAnnouncementType(formData);
    if (success) {
      toast.current?.show({
        severity: 'success',
        summary: th('common.success'),
        detail: th('announcement.successMessageDetailAnnouncementType'),
        life: 3000,
      });
      handleHide();
      refetch?.();
    }
  };

  const handleDelete = (id) => {
    if (!id) return;
    confirmDialog({
      message: th('warrantyClaimLimit.confirmDialog.message'),
      header: th('common.delete') || 'Sil',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await deleteAnnouncementType(id);
          toast.current?.show({
            severity: 'success',
            summary: th('warrantyClaimLimit.toastMessages.successSummary'),
            detail: th('warrantyClaimLimit.toastMessages.deleteSuccess'),
            life: 3000,
          });
          handleHide();
          refetch?.();
        } catch {
          toast.current?.show({
            severity: 'error',
            summary: th('warrantyClaimLimit.toastMessages.errorSummary'),
            detail: th('warrantyClaimLimit.toastMessages.deleteError'),
            life: 4000,
          });
        }
      },
    });
  };

  const filterFields = useMemo(
    () => [
      {
        field: 'type',
        headerName: th('announcements.columnHeaders.type'),
        type: 'select',
        options: TYPE_OPTIONS,
        optionLabel: 'label',
        optionValue: 'value',
      },
    ],
    [th, TYPE_OPTIONS]
  );


  const onApplyFilters = (uiFilters) => {
    const normalized = {
      ...uiFilters,
      type: toNumberType(uiFilters?.type),
    };
    handleFilterChange(normalized);
  };

  const onResetFilters = () => {
    initFilters();
  };

  const handleCreateClick = () => {
    setIsEdit(false);
    setSelectedItem(null);
    setDialogVisible(true);
  };

  return (
    <div className="card">
      <div className="flex justify-content-between align-items-center mb-4" />
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex justify-content-between align-items-center mb-4">
        <h1 className="m-0">{th('announcements.title')}</h1>
        <Button label={th('announcements.newTitle')} icon="pi pi-plus" onClick={handleCreateClick} />
      </div>

      <div className="mb-2">
        <GenericTableFilter
          key="announcement-type-filter"
          filterFields={filterFields}
          initialFilters={{ type: activeFilters?.type ?? '' }} 
          onSearch={() => {}}
          onApplyFilters={onApplyFilters}                      
          onResetFilters={onResetFilters}
        />
      </div>

      <AnnouncementTypeTable
        data={announcementTypeApi}
        totalCount={announcementTypeApi?.totalCount}
        loading={isLoading}
        lazyParams={lazyParams}
        onPage={onPage}
        onEdit={handleEdit}
      />

      {dialogVisible && (
        <AnnouncementTypeForm
          visible={dialogVisible}
          onHide={handleHide}
          onSubmit={handleSubmit}
          initialData={selectedItem}
          loading={isLoading}
          handleDelete={handleDelete}
          isEdit={isEdit}
        />
      )}
    </div>
  );
};

export default AnnouncementTypePage;
